/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import Button from "react-bootstrap/Button";

import "react-quill/dist/quill.snow.css";

import get from "lodash/get";
import set from "lodash/set";
import map from "lodash/map";

import Select from "react-select";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { QUERY_DIGITAL_RECEPTION_SETTINGS } from "../../../../../config/graphql/query";
import { UPDATE_DIGITAL_RECEPTION_SETTINGS } from "../../../../../config/graphql/mutation";

import { reactSelectCustomStyles } from "../../../../Employees/Employee/Information";

import {
  LayoutOptionController,
  LayoutOptionSvg,
  LayoutOptionText,
} from "../../../../../components/DigitalReception/Layout";

import LayoutWayfindingOptions from "./WayfindingOptions";

const MultiWayfinding = () => {
  const methods = useForm<IDigitalReceptionLayout>({
    shouldUnregister: false,
  });

  const [language, setLanguage] = useState<string>();

  const { screenId } = useParams<{ screenId: string }>();

  const [iFrameSource, setIFrameSource] = useState<string>();

  const previewRef = useRef<HTMLIFrameElement>(null);

  const { t } = useTranslation(["screens", "common"]);

  const { data: screenData, refetch } = useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION_SETTINGS, {
    skip: !screenId,
    variables: {
      id: screenId,
      language,
    },
    onCompleted: ({
      digitalReception: { settings, languages, language: lng },
    }) => {
      if (!settings?.layout?.multiWayfinding) {
        return;
      }

      setLanguage(language && languages.includes(language) ? language : lng);

      methods.reset(settings.layout);
    },
  });

  useEffect(() => {
    if (screenData?.digitalReception?.previewUrl && !iFrameSource) {
      setIFrameSource(
        `${screenData?.digitalReception?.previewUrl}`?.replace(
          ":8080",
          ":9000",
        ),
      );
    }
  }, [screenData]);

  const languageOptions = useMemo(
    () =>
      map(screenData?.digitalReception.languages, (value) => ({
        value,
        label: t(`languages:${value}`),
      })) ?? [],
    [screenData, t],
  );

  useEffect(() => {
    refetch({ id: screenId, language });
  }, [language, refetch, screenId]);

  const postMessage = useCallback(
    (message: string, targetOrigin = "*") => {
      return previewRef?.current?.contentWindow?.postMessage(
        message,
        targetOrigin,
      );
    },
    [previewRef],
  );

  useEffect(() => {
    const message = JSON.stringify({
      action: "update-language",
      data: language,
    });

    postMessage(message);
  }, [language]);

  const onChangePreview = useCallback(
    (name: string, value: any) => {
      const data = {};
      set(
        data,
        ["digitalReception", "settings", "layout", ...name.split(".")],
        value,
      );

      const message = JSON.stringify({
        action: "update-digitalReception",
        data,
      });

      return postMessage(message);
    },
    [postMessage],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.action === "reception-ready") {
          setTimeout(() => {
            const messageData = {};
            set(
              messageData,
              ["digitalReception", "metadata", "showCursor"],
              true,
            );

            const message = JSON.stringify({
              action: "update-digitalReception",
              data: messageData,
            });

            postMessage(message);
          }, 1000);
        }
      } catch (e) {
        console.error("Invalid message data", e);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [postMessage]);

  const [onUpdate] = useMutation(UPDATE_DIGITAL_RECEPTION_SETTINGS, {
    refetchQueries: [
      {
        query: QUERY_DIGITAL_RECEPTION_SETTINGS,
        variables: { id: screenId, language },
      },
    ],
  });

  const onSubmit = useCallback(
    async (layout: IDigitalReceptionLayout) => {
      const nextLayout: IDigitalReceptionLayout = {};

      nextLayout.multiWayfinding = {
        enabled: get(layout, "multiWayfinding.enabled") || false,
        text: get(layout, "multiWayfinding.text") || "",
        svg: get(layout, "multiWayfinding.svg") || undefined,
        wayfindingOptions: (
          get(layout, "multiWayfinding.wayfindingOptions") || []
        ).map((opt: IDigitalReceptionWayfindingOption) => {
          return {
            label: opt.label || "",
            instructionLabel: opt.instructionLabel || "",
            svg: opt.svg || undefined,
            colors: {
              text: opt.colors?.text || undefined,
              background: opt.colors?.background || undefined,
            },
          };
        }),
      };

      onUpdate({
        variables: {
          input: {
            id: screenId,
            language,
            settings: {
              layout: nextLayout,
            },
          },
        },
      })
        .then(() => {
          toast.success<string>(t("screens:screen.screenRoute.toast.updated"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [onUpdate, t, screenId, language],
  );

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <div className="row">
          <div className="col-lg-8">
            <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="col-lg-5">
                <div className="px-0">
                  <h6 className="mb-2">
                    {t("screens:screen.settings.layout.language")}
                  </h6>
                  <div className="mb-4">
                    <Select
                      options={languageOptions}
                      value={languageOptions.find(
                        (option) => option.value === language,
                      )}
                      onChange={(value) => {
                        setLanguage(value?.value);
                      }}
                      styles={reactSelectCustomStyles(false)}
                    />
                  </div>
                </div>
                <LayoutOptionController
                  name="multiWayfinding"
                  label={t(
                    "screens:screen.settings.layout.multiWayfinding.enable",
                  )}
                  onChangePreview={onChangePreview}
                  language={language}
                >
                  <h6 className="my-3">
                    {t("screens:screen.settings.layout.multiWayfinding.title")}
                  </h6>
                  <div className="mt-3">
                    {t("screens:screen.settings.layout.multiWayfinding.text")}
                  </div>
                  <LayoutOptionText
                    name="multiWayfinding"
                    onChangePreview={onChangePreview}
                    language={language ?? "en"}
                  />
                  <LayoutOptionSvg
                    name="multiWayfinding"
                    label={t(
                      "screens:screen.settings.layout.multiWayfinding.svg",
                    )}
                    onChangePreview={onChangePreview}
                    availableSvgs={
                      screenData?.digitalReception?.settings?.layout
                        ?.multiWayfinding?.availableSvgs
                    }
                  />
                </LayoutOptionController>
                <p className="text-danger">
                  {t("screens:screen.settings.layout.multiWayfinding.saveTip")}
                </p>
                <Button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "6rem" }}
                >
                  {t("screens:screen.settings.layout.multiWayfinding.save")}
                </Button>
              </div>
              <div className="col-lg-7">
                <LayoutWayfindingOptions
                  onChangePreview={onChangePreview}
                  availableSvgs={
                    screenData?.digitalReception?.settings?.layout
                      ?.multiWayfinding?.availableSvgs
                  }
                  language={language}
                />
              </div>
            </form>
          </div>

          <div className="col-lg-4 mt-4">
            <div className="col-12 mb-2">
              {/* <div
                className=" position-absolute p-0"
                style={{ zIndex: 100, width: 600 / (1920 / 1080), height: 600 }}
              /> */}
              <iframe
                id="preview"
                ref={previewRef}
                title={`screen-${screenId}`}
                className="border border-dark"
                src={`${iFrameSource?.replace("8080", "9000")}`}
                style={{
                  width: 600 / (1920 / 1080),
                  height: 600,
                }}
                frameBorder={0}
                marginHeight={0}
                marginWidth={0}
              />
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default MultiWayfinding;
